import * as _ from "lodash";
import { IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import {
    WB_HierarchyRow,
    WB_HierarchyLevelRow,
    WB_HierarchySalesAreaRow,
    WB_HierarchyPeriodRow,
    WB_SalesAreaRow,
    WB_ScheduleRow,
    WB_HierarchyEntityGenerationRow,
    WB_HierarchyAttributeGenerationRow,
    WB_HierarchyIntelligentKeyRow,
    WB_HierarchyMDScopeRow,
} from "../PA.Core/PAdbSchemaRows";
import { IwbHierarchy } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Hierarchy,
    esWB_Hierarchy_Attribute_Generation,
    esWB_Hierarchy_Entity_Generation,
    esWB_Hierarchy_Intelligent_Key,
    esWB_Hierarchy_Level,
    esWB_Hierarchy_Period,
    esWB_Hierarchy_SalesArea,
    esWB_Hierarchy_MDScope,
    esWB_SalesArea,
    esWB_Schedule,
} from "../PA.Core/PAdbSchema";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";

import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { HierarchyBOValidations } from "./HierarchyBO.Validations";
import { DefaultChanges } from "../PA.Core/BusinessObject/BusinessObjectChanges";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { BehaviorSubject } from "rxjs";
import { RecordUtils } from "../PA.Core/Basic/Records";
import { IHierarchyStructure } from "../PA.MD.Core/PA.MD.IF.Hierarchy";
import { HierarchyStructureManager } from "../PA.MD.Hierarchy/HierarchyStructureManager";
import { IJobParam, IJobParamCreateLoadOptions } from "../PA.Job.Core/PA.Job.IF.JobParam";
import { JobParamManager } from "../PA.Job.Core/JobParamManager";
import { CustomerHierarchyBO } from "./CustomerHierarchyBO";
import { ProductHierarchyBO } from "./ProductHierarchyBO";
import { PeriodHierarchyBO } from "./PeriodHierarchyBO";

export interface IHierarchyChangedEvent {
    HierarchyBO: HierarchyBO;
    Row: WB_HierarchyAttributeGenerationRow;
    PropertyChanged: string;
    PropertyValue: any;
}

export interface ILevelPropertyChangedEvent {
    HierarchyBO: HierarchyBO;
    Row: WB_HierarchyLevelRow;
    PropertyChanged: string;
    PropertyValue: any;
}

export interface IIntlKeyPropertyChangedEvent {
    HierarchyBO: HierarchyBO;
    Row: WB_HierarchyIntelligentKeyRow;
    PropertyChanged: string;
    PropertyValue: any;
}

export interface IHierarchyMainPropertyChangeEvent {
    HierarchyBO: HierarchyBO;
    Row: WB_HierarchyRow;
    PropertyChanged: string;
    PropertyValue: any;
}

export class HierarchyBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Hierarchy: WB_HierarchyRow;
    _HierarchyChanged: BehaviorSubject<IHierarchyChangedEvent>;
    _HierarchyPropertyChange: BehaviorSubject<IHierarchyMainPropertyChangeEvent>;

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Hierarchy" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Hierarchy = null;
        this._HierarchyChanged = new BehaviorSubject<IHierarchyChangedEvent>(undefined);
        this._HierarchyPropertyChange = new BehaviorSubject<IHierarchyMainPropertyChangeEvent>(undefined);
        this._LevelPropertyChanged = new BehaviorSubject<ILevelPropertyChangedEvent>(undefined);
        this._LevelCollectionChanged = new BehaviorSubject<HierarchyBO>(this);
        this._IntlKeyPropertyChanged = new BehaviorSubject<IIntlKeyPropertyChangedEvent>(undefined);

        this.IHaveSalesAreas_init();
    }

    get WB_Hierarchy(): WB_HierarchyRow {
        return this.drWB_Hierarchy;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        self.LoadProgressProvider.start("Creating Hierarchy");
        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                // "Event already loaded.";
                resolve(self);
                return;
            }
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for new Hierarchy.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            self.dbContext
                .createRecordWithKey(esWB_Hierarchy)
                .then((newHierarchyRow: WB_HierarchyRow) => {
                    newHierarchyRow.setDefaults();
                    self.drWB_Hierarchy = newHierarchyRow;
                    self.drWB_Hierarchy.cif.WB_MDSET_ID = self.LoadOptions?.wbMDSetId || 0;
                    self.drWB_Hierarchy.cif.HIERARCHY_STATUS = "Initial";
                    self.dbContext.add(esWB_Hierarchy, newHierarchyRow);
                    self.Identity.setAll(
                        self.WB_Hierarchy.cif.WB_HIERARCHY_ID,
                        self.WB_Hierarchy.cif.HIERARCHY_ID,
                        self.WB_Hierarchy.cif.HIERARCHY_NAME,
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_Hierarchy,
                        "WB_HIERARCHY_ID",
                        "HIERARCHY_ID",
                        "HIERARCHY_NAME",
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.enableRecordWriteBack(self.WB_Hierarchy, "HIERARCHY_ID", "HIERARCHY_NAME");

                    return self._AfterBoLoadCompleteInternal();
                })
                .then(() => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Hierarchy already loaded.");
                self._OnLoadComplete = null;
                resolve(self);
                return;
            }

            self.LoadOptions = options || {};

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Hierarchy.");
                return;
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = HierarchyBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = HierarchyBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    const aHierarchyRow: any = recordResolver.get(esWB_Hierarchy, anId);

                    if (_.isEmpty(aHierarchyRow)) {
                        error("Hierarchy not found");
                        return;
                    }

                    self.drWB_Hierarchy = aHierarchyRow;
                    self.Identity.setAll(
                        self.WB_Hierarchy.cif.WB_HIERARCHY_ID,
                        self.WB_Hierarchy.cif.HIERARCHY_ID,
                        self.WB_Hierarchy.cif.HIERARCHY_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_Hierarchy,
                        "WB_HIERARCHY_ID",
                        "HIERARCHY_ID",
                        "HIERARCHY_NAME",
                    );
                    self.Identity.enableRecordWriteBack(self.WB_Hierarchy, "HIERARCHY_ID", "HIERARCHY_NAME");
                    return self._AfterBoLoadCompleteInternal();
                })
                .then(() => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function (err: any) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    private async _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this;

        self.WB_Hierarchy.setupOnChangeEvents([], self.onHierarchyMainPropertyChange.bind(self), self);
        _.forEach(self.WB_Hierarchy.WB_HierarchyLevelRows, (aRow) => {
            aRow.setupOnChangeEvents([], self.onLevelRecordChange.bind(self), self);
        });
        _.forEach(self.WB_Hierarchy.WB_HierarchyIntelligentKeyRows, (aRow) => {
            aRow.setupOnChangeEvents([], self.onIntlKeyRecordChange.bind(self), self);
        });

        await self.addHierarchyPeriod({
            periodStartDate: DateUtils.getMinSAPBWDate(),
            periodEndDate: DateUtils.getMaxSAPBWDate(),
            bInit: true,
        });

        if (_.isEmpty(self.WB_Hierarchy.WB_ScheduleRow) || self.WB_Hierarchy.cif.WB_SCHEDULE_ID === 0) {
            const aSchedule = <WB_ScheduleRow>await self.dbContext.createRecordWithKey(esWB_Schedule);
            if (!_.isEmpty(aSchedule)) {
                self.WB_Hierarchy.cif.WB_SCHEDULE_ID = aSchedule.cif.WB_SCHEDULE_ID;
                self.WB_Hierarchy.WB_ScheduleRow = aSchedule;
            }
        }

        if (
            _.includes(["Customer", "Product"], self.WB_Hierarchy.cif.ENTITY_DIMENSION) &&
            self.WB_Hierarchy.cif.HIERARCHY_WORKFLOW_MODE != "LandingZone" &&
            _.isEmpty(_.filter(self.WB_Hierarchy.WB_HierarchyMDScopeRows, (row) => row.isActive))
        )
            await self.addHierarchyMDScope();

        await self.IJobParam_OnLoadComplete();
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    get HierarchyStructureLevels(): WB_HierarchyLevelRow[] {
        return _.orderBy(
            _.filter(this.WB_Hierarchy.WB_HierarchyLevelRows, (aRow) => aRow.isActive),
            "LEVEL_DEPTH",
        );
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            "Validating " + self.WB_Hierarchy.cif.HIERARCHY_NAME + "(" + self.WB_Hierarchy.cif.HIERARCHY_ID + ")",
        );
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == null) {
                const sError = "Unable to determine dbContext for to Validate Hierarchy.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }
            HierarchyBOValidations.ValidateEmptyFields(self)
                .then((ignore: any) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        const opts = options || {};
        opts.dctExcludeFields = opts.dctExcludeFields || {};
        opts.dctExcludeFields[esWB_Hierarchy] = ["HIERARCHY_STATUS"];
        opts.bIgnoreStandardFields = true;
        opts.dctIgnoreChanges = opts.dctIgnoreChanges || {};

        const changeSet = DefaultChanges(this, this.WB_Hierarchy, opts);
        return changeSet;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this;

        const src = self.WB_Hierarchy;

        const newBO = new HierarchyBO({ wbMDSetId: src.cif.WB_MDSET_ID });
        await newBO.LoadNew({});
        if (!newBO.bIsLoaded) return undefined;

        const tgt = newBO.WB_Hierarchy;
        tgt.cif.HIERARCHY_ID = src.cif.HIERARCHY_ID + "_Copy";
        tgt.cif.HIERARCHY_NAME = src.cif.HIERARCHY_NAME + "_Copy";

        RecordUtils.CopyProperties(
            self.dbContext,
            esWB_Hierarchy,
            src,
            tgt,
            [],
            [
                "HIERARCHY_ID",
                "HIERARCHY_NAME",
                "TARGET_WB_ID",
                "CREATED_DTTS",
                "MODIFIED_DTTS",
                "DELETED_DT",
                "DELETED_IND",
                "WB_SCHEDULE_ID",
            ],
            {},
        );

        // Add Levels
        for (const srcLvl of src.WB_HierarchyLevelRows) {
            if (!srcLvl.isActive) continue;

            const newLvl = await newBO.addHierarchyLevel();
            RecordUtils.CopyProperties(
                self.dbContext,
                esWB_Hierarchy_Level,
                srcLvl,
                newLvl,
                [],
                ["WB_HIERARCHY_ID", "CREATED_DTTS", "MODIFIED_DTTS", "DELETED_DT", "DELETED_IND"],
                {},
            );
        }

        // Add Maintenance Periods
        for (const srcPrd of src.WB_HierarchyPeriodRows) {
            if (!srcPrd.isActive) continue;

            if (srcPrd.cif.PERIOD_NAME == "INIT") continue;

            const newPrd = await newBO.addHierarchyPeriod();
            RecordUtils.CopyProperties(
                self.dbContext,
                esWB_Hierarchy_Period,
                srcPrd,
                newPrd,
                [],
                ["WB_HIERARCHY_ID", "CREATED_DTTS", "MODIFIED_DTTS", "DELETED_DT", "DELETED_IND"],
                {},
            );
        }

        // Add Enterprise Scope
        for (const srcSA of src.WB_HierarchySalesAreaRows) {
            if (!srcSA.isActive) continue;

            const newSA = await newBO.addHierarchySalesArea(srcSA.cif.WB_SALESAREA_ID);
        }

        // Add Intelligent Key
        for (const srcIK of src.WB_HierarchyIntelligentKeyRows) {
            if (!srcIK.isActive) continue;

            const newIK = await newBO.addHierarchyIntelligentKey();
            RecordUtils.CopyProperties(
                self.dbContext,
                esWB_Hierarchy_Intelligent_Key,
                srcIK,
                newIK,
                [],
                ["WB_HIERARCHY_ID", "CREATED_DTTS", "MODIFIED_DTTS", "DELETED_DT", "DELETED_IND"],
                {},
            );
        }

        // Entity Generation
        for (const srcEG of src.WB_HierarchyEntityRows) {
            if (!srcEG.isActive) continue;

            const newEG = await newBO.addHierarchyEntity();
            RecordUtils.CopyProperties(
                self.dbContext,
                esWB_Hierarchy_Entity_Generation,
                srcEG,
                newEG,
                [],
                ["WB_HIERARCHY_ID", "CREATED_DTTS", "MODIFIED_DTTS", "DELETED_DT", "DELETED_IND"],
                {},
            );
        }

        // Attribute Generation
        for (const srcAG of src.WB_HierarchyAttributeRows) {
            if (!srcAG.isActive) continue;

            const newAG = await newBO.addHierarchyAttribute();
            RecordUtils.CopyProperties(
                self.dbContext,
                esWB_Hierarchy_Attribute_Generation,
                srcAG,
                newAG,
                [],
                ["WB_HIERARCHY_ID", "CREATED_DTTS", "MODIFIED_DTTS", "DELETED_DT", "DELETED_IND"],
                {},
            );
        }

        await self._AfterBoLoadCompleteInternal();
        return newBO;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbHierarchy = <IwbHierarchy>self.drWB_Hierarchy;

        self.SaveProgressProvider.start("Saving " + wbHierarchy.HIERARCHY_NAME + "(" + wbHierarchy.HIERARCHY_ID + ")");

        try {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = "Unable to determine dbContext to Save Hierarchy.";
                self.SaveProgressProvider.exception(sError);
                return self;
            }
            self.SaveOptions = options || {};

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any,
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            await self.refreshBuildStatus();

            await self.Validate({});

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            if (self.Changes().bHasChanges)
                self.WB_Hierarchy.cif.MODIFIED_DTTS = DateUtils.convertToSAPBWTs(new Date());

            const tasks: Promise<any>[] = [];
            tasks.push(saveFunction(esWB_Hierarchy, self.drWB_Hierarchy, {}));
            tasks.push(saveFunction(esWB_Hierarchy_Level, self.drWB_Hierarchy.WB_HierarchyLevelRows, {}));
            tasks.push(saveFunction(esWB_Hierarchy_Entity_Generation, self.drWB_Hierarchy.WB_HierarchyEntityRows, {}));
            tasks.push(
                saveFunction(esWB_Hierarchy_Attribute_Generation, self.drWB_Hierarchy.WB_HierarchyAttributeRows, {}),
            );
            tasks.push(saveFunction(esWB_Hierarchy_Period, self.drWB_Hierarchy.WB_HierarchyPeriodRows, {}));
            tasks.push(saveFunction(esWB_Hierarchy_SalesArea, self.drWB_Hierarchy.WB_HierarchySalesAreaRows, {}));
            tasks.push(
                saveFunction(esWB_Hierarchy_Intelligent_Key, self.drWB_Hierarchy.WB_HierarchyIntelligentKeyRows, {}),
            );
            tasks.push(saveFunction(esWB_Hierarchy_MDScope, self.drWB_Hierarchy.WB_HierarchyMDScopeRows, {}));
            tasks.push(saveFunction(esWB_Schedule, self.drWB_Hierarchy.WB_ScheduleRow, {}));
            await Promise.all(tasks);

            if (!self.JobParamForExport || !self.JobParamForBuilder) {
                if (self.WB_Hierarchy.isActive) {
                    await self.IJobParam_OnLoadComplete();
                }
            }

            // make sure the job parameters have the latest values
            if (self.JobParamForExport) {
                self.JobParamForExport.id_ui = self.WB_Hierarchy.cif.HIERARCHY_ID + "_Export";
                self.JobParamForExport.name = self.WB_Hierarchy.cif.HIERARCHY_NAME + "_Export";
                self.JobParamForExport.WB_JobParamRow.cif.WB_MDSET_ID = self.WB_Hierarchy.cif.WB_MDSET_ID;
                await self.JobParamForExport.Save(bBatch);
            }
            if (self.JobParamForBuilder) {
                self.JobParamForBuilder.id_ui = self.WB_Hierarchy.cif.HIERARCHY_ID + "_Builder";
                self.JobParamForBuilder.name = self.WB_Hierarchy.cif.HIERARCHY_NAME + "_Builder";
                self.JobParamForBuilder.WB_JobParamRow.cif.WB_MDSET_ID = self.WB_Hierarchy.cif.WB_MDSET_ID;
                self.JobParamForBuilder.WB_JobParamRow.cif.DISABLED_IND =
                    self.WB_Hierarchy.executionMode === 1 ? "1" : "0"; // batch: 0, manually: 1
                await self.JobParamForBuilder.Save(bBatch);
            }

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
            return self;
        } catch (err) {
            self.SaveProgressProvider.exception(err);
            return self;
        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbHierarchy = <IwbHierarchy>self.drWB_Hierarchy;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " + wbHierarchy.HIERARCHY_NAME + "(" + wbHierarchy.WB_HIERARCHY_ID + ")",
        );

        try {
            // 1. Validate if we can delete the Hierarchy

            // 2. Delete
            if (self.drWB_Hierarchy.cif.TARGET_WB_ID > 0) {
                const hs = await HierarchyStructureManager.getHierarchyStructure(
                    self.drWB_Hierarchy.cif.WB_HIERARCHY_ID,
                    { dbContext: self.dbContext },
                );
                const entityDimension = self.drWB_Hierarchy.cif.ENTITY_DIMENSION;
                if (hs) {
                    await hs.Delete();

                    switch (entityDimension) {
                        case "Customer": {
                            const custH = new CustomerHierarchyBO();
                            await custH.Load(self.drWB_Hierarchy.cif.TARGET_WB_ID);
                            await custH.Delete();
                            break;
                        }
                        case "Product": {
                            const prdH = new ProductHierarchyBO();
                            await prdH.Load(self.drWB_Hierarchy.cif.TARGET_WB_ID);
                            await prdH.Delete();
                            break;
                        }
                        case "Period": {
                            const periodH = new PeriodHierarchyBO();
                            await periodH.Load(self.drWB_Hierarchy.cif.TARGET_WB_ID);
                            await periodH.Delete();
                            break;
                        }
                    }

                    if (!hs.bDeleted) {
                        self.DeleteMessageProvider.addMessages(hs.DeleteMessageProvider.Messages);
                        return;
                    }
                }
            }

            // 3. If Valid, mark all the records for deletion.
            self.WB_Hierarchy.markDeleted();
            _.forEach(self.WB_Hierarchy.WB_HierarchyLevelRows, (aRow) => {
                if (aRow.isActive) aRow.markDeleted();
            });

            _.forEach(self.WB_Hierarchy.WB_HierarchySalesAreaRows, (aRow) => {
                if (aRow.isActive) aRow.markDeleted();
            });

            _.forEach(self.WB_Hierarchy.WB_HierarchyPeriodRows, (aRow) => {
                if (aRow.isActive) aRow.markDeleted();
            });

            _.forEach(self.WB_Hierarchy.WB_HierarchyEntityRows, (aRow) => {
                if (aRow.isActive) aRow.markDeleted();
            });

            _.forEach(self.WB_Hierarchy.WB_HierarchyAttributeRows, (aRow) => {
                if (aRow.isActive) aRow.markDeleted();
            });

            _.forEach(self.WB_Hierarchy.WB_HierarchyMDScopeRows, (aRow) => {
                if (aRow.isActive) aRow.markDeleted();
            });

            if (self.JobParamForExport) {
                await self.JobParamForExport.Delete(bSaveNow);
            }
            if (self.JobParamForBuilder) {
                await self.JobParamForBuilder.Delete(bSaveNow);
            }

            if (bSaveNow) {
                await self.SaveUsing(false);
            }

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            this.DeleteProgressProvider.exception(err);
        }

        return self;
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //<editor-fold desc="Record Resolver -- static">
    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const hierarchy_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
        };

        const aRecResolver = new ESRecordResolver(dbContext, hierarchy_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Hierarchy, ids)
                .then((result) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc="Hierarchy Record Changes">

    get HierarchyPropertyChanged(): BehaviorSubject<IHierarchyChangedEvent> {
        return this._HierarchyChanged;
    }

    private onHierarchyAttributeGenerationChange(
        record: WB_HierarchyAttributeGenerationRow,
        propertyName: string,
        propertyValue: any,
        options: HierarchyBO,
    ) {
        this.HierarchyPropertyChanged.next({
            HierarchyBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue,
        });
    }

    get MainPropertyChanged(): BehaviorSubject<IHierarchyMainPropertyChangeEvent> {
        return this._HierarchyPropertyChange;
    }

    private onHierarchyMainPropertyChange(
        record: WB_HierarchyRow,
        propertyName: string,
        propertyValue: any,
        options: HierarchyBO,
    ) {
        if (propertyName == "HIERARCHY_WORKFLOW_MODE")
            this.MainPropertyChanged.next({
                HierarchyBO: options,
                Row: record,
                PropertyChanged: propertyName,
                PropertyValue: propertyValue,
            });
    }

    //</editor-fold>

    //<editor-fold desc="Refresh Status">
    async refreshBuildStatus(): Promise<void> {
        const self = this;
        const hr = self.WB_Hierarchy;
        if (hr.isNew()) return;
        if (!hr.isActive) return;

        const dbCtx = new PAdbContext();
        const dbRow = <WB_HierarchyRow>await dbCtx.find(esWB_Hierarchy, self.WB_Hierarchy.cif.WB_HIERARCHY_ID);
        if (_.isEmpty(dbRow)) return;
        if (!dbRow.isActive) return;
        if (dbRow.cif.TARGET_WB_ID > 0 && hr.cif.TARGET_WB_ID != dbRow.cif.TARGET_WB_ID)
            hr.cif.TARGET_WB_ID = dbRow.cif.TARGET_WB_ID;

        if (hr.cif.HIERARCHY_STATUS != dbRow.cif.HIERARCHY_STATUS) hr.cif.HIERARCHY_STATUS = dbRow.cif.HIERARCHY_STATUS;
    }

    //</editor-fold>

    //<editor-fold desc="Sales Area">

    // Sales Area changes
    _salesAreasChanged: BehaviorSubject<HierarchyBO>;

    IHaveSalesAreas_init() {
        this._salesAreasChanged = new BehaviorSubject<HierarchyBO>(this);
    }

    get SalesAreasChanged(): BehaviorSubject<HierarchyBO> {
        return this._salesAreasChanged;
    }

    get bHasSalesAreasAdded(): boolean {
        if (_.filter(this.WB_Hierarchy.WB_HierarchySalesAreaRows, (aRow) => aRow.isActive).length) return true;
        else return false;
    }

    //</editor-fold>

    private _LevelPropertyChanged: BehaviorSubject<ILevelPropertyChangedEvent>;
    private _LevelCollectionChanged: BehaviorSubject<HierarchyBO>;

    addHierarchyLevel(): Promise<WB_HierarchyLevelRow> {
        const self = this;
        let newRow: WB_HierarchyLevelRow = undefined;
        return new Promise<WB_HierarchyLevelRow>((resolve, error) => {
            // create record
            self.dbContext
                .createRecordWithKey(esWB_Hierarchy_Level)
                .then((aNewRow: WB_HierarchyLevelRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_HIERARCHY_ID = self.WB_Hierarchy.cif.WB_HIERARCHY_ID;

                    let idx = 0;
                    _.forEach(
                        _.orderBy(
                            _.filter(
                                self.WB_Hierarchy.WB_HierarchyLevelRows,
                                (aRow) => aRow.isActive && aRow.cif.LEVEL_DEPTH != 0,
                            ),
                            "LEVEL_DEPTH",
                            "asc",
                        ),
                        (aRow) => {
                            idx++;
                            aRow.cif.LEVEL_DEPTH = idx;
                        },
                    );
                    aNewRow.cif.LEVEL_DEPTH = idx + 1;
                    self.dbContext.add(esWB_Hierarchy_Level, aNewRow);
                    aNewRow.setupOnChangeEvents([], self.onLevelRecordChange.bind(self), self);
                    newRow = aNewRow;
                    self.LevelCollectionChanged.next(self);
                    if (
                        self.WB_Hierarchy.cif.HIERARCHY_WORKFLOW_MODE != "LandingZone" &&
                        _.isEmpty(_.filter(self.WB_Hierarchy.WB_HierarchyEntityRows, (row) => row.isActive))
                    )
                        return self.addHierarchyMDScope();
                })
                .then(() => {
                    resolve(newRow);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeHierarchyLevel(wbHierarchyLevelId: number): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            _.forEach(self.WB_Hierarchy.WB_HierarchyLevelRows, (aRow) => {
                if (aRow.isActive && aRow.cif.WB_HIERARCHYLR_ID == wbHierarchyLevelId) {
                    aRow.markDeleted();
                }
            });
            let idx = 0;
            _.forEach(
                _.orderBy(
                    _.filter(self.WB_Hierarchy.WB_HierarchyLevelRows, (aRow) => aRow.isActive),
                    "LEVEL_DEPTH",
                    "asc",
                ),
                (aRow) => {
                    idx++;
                    aRow.cif.LEVEL_DEPTH = idx;
                },
            );

            self.LevelCollectionChanged.next(self);
            if (_.isEmpty(_.filter(self.WB_Hierarchy.WB_HierarchyLevelRows, (row) => row.isActive))) {
                _.forEach(self.WB_Hierarchy.WB_HierarchyMDScopeRows, (scope) => {
                    if (scope.isActive) scope.markDeleted();
                });
            }
            resolve();
        });
    }

    private onLevelRecordChange(record: WB_HierarchyLevelRow, propertyName: string, propertyValue: any, options: any) {
        if (propertyName == "WB_ENTITY_ID")
            this.LevelPropertyChanged.next({
                HierarchyBO: options,
                Row: record,
                PropertyChanged: propertyName,
                PropertyValue: propertyValue,
            });
    }

    get LevelPropertyChanged(): BehaviorSubject<ILevelPropertyChangedEvent> {
        return this._LevelPropertyChanged;
    }

    get LevelCollectionChanged(): BehaviorSubject<HierarchyBO> {
        return this._LevelCollectionChanged;
    }

    get Leaf_Level(): WB_HierarchyLevelRow {
        let rv: WB_HierarchyLevelRow = undefined;
        _.forEach(this.WB_Hierarchy.WB_HierarchyLevelRows, (a) => {
            if (!a.isActive) return;

            if (_.isEmpty(rv)) rv = a;

            if (a.cif.LEVEL_DEPTH > rv.cif.LEVEL_DEPTH) rv = a;
        });

        return rv;
    }

    get Leaf_WB_Entity_ID(): number {
        return this.Leaf_Level?.cif?.WB_ENTITY_ID || 0;
    }

    get Leaf_WB_Entity_ID_Enterprise(): number {
        return this.Leaf_Level?.cif?.WB_ENTITY_ID_ENTERPRISE || 0;
    }

    addHierarchySalesArea(wbSalesAreaId: number): Promise<WB_HierarchySalesAreaRow> {
        const self = this;
        return new Promise<WB_HierarchySalesAreaRow>((resolve, error) => {
            let found = undefined;
            _.forEach(self.WB_Hierarchy.WB_HierarchySalesAreaRows, (aRow) => {
                if (aRow.isActive && aRow.cif.WB_SALESAREA_ID == wbSalesAreaId) {
                    found = aRow;
                    return;
                }
            });
            if (found) {
                resolve(found);
                return;
            }
            let saleAreaRow: WB_SalesAreaRow = undefined;
            const aRecResolver = new ESRecordResolver(self.dbContext, {});
            aRecResolver
                .findAllWithRelationships(esWB_SalesArea, [wbSalesAreaId])
                .then((arrSaleArea: WB_SalesAreaRow[]) => {
                    if (arrSaleArea.length) saleAreaRow = arrSaleArea[0];
                    return self.dbContext.createRecordWithKey(esWB_Hierarchy_SalesArea);
                })
                .then((aNewRow: WB_HierarchySalesAreaRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_HIERARCHY_ID = self.WB_Hierarchy.cif.WB_HIERARCHY_ID;
                    aNewRow.cif.WB_SALESAREA_ID = wbSalesAreaId;
                    aNewRow.WB_SalesAreaRow = saleAreaRow;
                    self.dbContext.add(esWB_Hierarchy_SalesArea, aNewRow);
                    self.SalesAreasChanged.next(self);
                    resolve(aNewRow);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeHierarchySalesArea(wbHierarchySalesAreaId: number): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            _.forEach(self.WB_Hierarchy.WB_HierarchySalesAreaRows, (aRow) => {
                if (aRow.isActive && aRow.cif.WB_HIERARCHYSR_ID == wbHierarchySalesAreaId) {
                    aRow.markDeleted();
                }
            });
            self.SalesAreasChanged.next(self);
            resolve();
        });
    }

    addHierarchyPeriod(opts?: { [id: string]: any }): Promise<WB_HierarchyPeriodRow> {
        const self = this;
        return new Promise<WB_HierarchyPeriodRow>((resolve, error) => {
            if (!_.isEmpty(opts) && "bInit" in opts) {
                if (
                    _.filter(
                        self.WB_Hierarchy.WB_HierarchyPeriodRows,
                        (aRow) => aRow.isActive && aRow.cif.INIT_IND == "1",
                    ).length
                ) {
                    resolve(
                        _.filter(
                            self.WB_Hierarchy.WB_HierarchyPeriodRows,
                            (aRow) => aRow.isActive && aRow.cif.INIT_IND == "1",
                        )[0],
                    );
                    return;
                }
            }
            // create record
            self.dbContext
                .createRecordWithKey(esWB_Hierarchy_Period)
                .then((aNewRow: WB_HierarchyPeriodRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_HIERARCHY_ID = self.WB_Hierarchy.cif.WB_HIERARCHY_ID;
                    if (!_.isEmpty(opts)) {
                        if ("name" in opts) aNewRow.cif.PERIOD_NAME = opts.name;

                        if ("periodEndDate" in opts) aNewRow.cif.PERIOD_END_DT = opts.periodEndDate;

                        if ("periodStartDate" in opts) aNewRow.cif.PERIOD_START_DT = opts.periodStartDate;

                        if ("bInit" in opts) {
                            aNewRow.cif.INIT_IND = "1";
                            aNewRow.cif.ACTIVE_IND = "1";
                            aNewRow.cif.PERIOD_NAME = "INIT";
                        }
                    }
                    self.dbContext.add(esWB_Hierarchy_Period, aNewRow);
                    resolve(aNewRow);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeHierarchyPeriod(wbHierarchyPeriodId: number): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            _.forEach(self.WB_Hierarchy.WB_HierarchyPeriodRows, (aRow) => {
                if (aRow.isActive && aRow.cif.WB_HIERARCHYMPR_ID == wbHierarchyPeriodId) {
                    aRow.markDeleted();
                }
            });
            resolve();
        });
    }

    async addHierarchyAttribute(): Promise<WB_HierarchyAttributeGenerationRow> {
        const self = this;

        const aNewRow: WB_HierarchyAttributeGenerationRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Hierarchy_Attribute_Generation)
        );
        aNewRow.setDefaults();
        aNewRow.cif.WB_HIERARCHY_ID = self.drWB_Hierarchy.cif.WB_HIERARCHY_ID;
        aNewRow.WB_HierarchyRow = self.drWB_Hierarchy;
        self.dbContext.add(esWB_Hierarchy_Attribute_Generation, aNewRow);
        //   aNewRow.setupOnChangeEvents([], self.onHierarchyAttributeGenerationChange.bind(self), {})
        return aNewRow;
    }

    async removeHierarchyAttribute(wbHierarchyAttributeId: number): Promise<void> {
        const self = this;
        const found = false;

        _.forEach(self.drWB_Hierarchy.WB_HierarchyAttributeRows, (aHERow) => {
            if (aHERow.isActive && aHERow.cif.WB_HIERARCHYAGR_ID == wbHierarchyAttributeId) {
                aHERow.markDeleted();
                return;
            }
        });
    }

    async addHierarchyEntity(): Promise<WB_HierarchyEntityGenerationRow> {
        const self = this;

        const aNewRow: WB_HierarchyEntityGenerationRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Hierarchy_Entity_Generation)
        );
        aNewRow.setDefaults();
        aNewRow.cif.WB_HIERARCHY_ID = self.drWB_Hierarchy.cif.WB_HIERARCHY_ID;
        aNewRow.WB_HierarchyRow = self.drWB_Hierarchy;
        self.dbContext.add(esWB_Hierarchy_Entity_Generation, aNewRow);
        return aNewRow;
    }

    async removeHierarchyEntity(wbHierarchyEntityId: number): Promise<void> {
        const self = this;
        const found = false;

        _.forEach(self.drWB_Hierarchy.WB_HierarchyEntityRows, (aHERow) => {
            if (aHERow.isActive && aHERow.cif.WB_HIERARCHYEGR_ID == wbHierarchyEntityId) {
                aHERow.markDeleted();
                return;
            }
        });
    }

    addHierarchyIntelligentKey(): Promise<WB_HierarchyIntelligentKeyRow> {
        const self = this;
        return new Promise<WB_HierarchyLevelRow>((resolve, error) => {
            // create record
            self.dbContext
                .createRecordWithKey(esWB_Hierarchy_Intelligent_Key)
                .then((aNewRow: WB_HierarchyLevelRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_HIERARCHY_ID = self.WB_Hierarchy.cif.WB_HIERARCHY_ID;

                    let idx = 0;
                    _.forEach(
                        _.orderBy(
                            _.filter(
                                self.WB_Hierarchy.WB_HierarchyIntelligentKeyRows,
                                (aRow) => aRow.isActive && aRow.cif.LEVEL_DEPTH != 0,
                            ),
                            "LEVEL_DEPTH",
                            "asc",
                        ),
                        (aRow) => {
                            idx++;
                            aRow.cif.LEVEL_DEPTH = idx;
                        },
                    );
                    aNewRow.cif.LEVEL_DEPTH = idx + 1;
                    self.dbContext.add(esWB_Hierarchy_Intelligent_Key, aNewRow);
                    aNewRow.setupOnChangeEvents([], self.onIntlKeyRecordChange.bind(self), self);
                    resolve(aNewRow);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeHierarchyIntelligentKey(wbHierarchyIKId: number): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            _.forEach(self.WB_Hierarchy.WB_HierarchyIntelligentKeyRows, (aRow) => {
                if (aRow.isActive && aRow.cif.WB_HIERARCHYIKR_ID == wbHierarchyIKId) {
                    aRow.markDeleted();
                }
            });
            let idx = 0;
            _.forEach(
                _.orderBy(
                    _.filter(self.WB_Hierarchy.WB_HierarchyIntelligentKeyRows, (aRow) => aRow.isActive),
                    "LEVEL_DEPTH",
                    "asc",
                ),
                (aRow) => {
                    idx++;
                    aRow.cif.LEVEL_DEPTH = idx;
                },
            );
            resolve();
        });
    }

    private _IntlKeyPropertyChanged: BehaviorSubject<IIntlKeyPropertyChangedEvent>;

    private onIntlKeyRecordChange(
        record: WB_HierarchyIntelligentKeyRow,
        propertyName: string,
        propertyValue: any,
        options: any,
    ) {
        if (propertyName == "WB_ENTITY_ID")
            this.IntlKeyPropertyChanged.next({
                HierarchyBO: options,
                Row: record,
                PropertyChanged: propertyName,
                PropertyValue: propertyValue,
            });
    }

    get IntlKeyPropertyChanged(): BehaviorSubject<IIntlKeyPropertyChangedEvent> {
        return this._IntlKeyPropertyChanged;
    }

    addHierarchyMDScope(): Promise<HierarchyBO> {
        const self = this;
        return new Promise<HierarchyBO>((resolve, error) => {
            // create record
            self.dbContext
                .createRecordWithKey(esWB_Hierarchy_MDScope)
                .then((aNewRow: WB_HierarchyMDScopeRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_HIERARCHY_ID = self.drWB_Hierarchy.cif.WB_HIERARCHY_ID;
                    aNewRow.WB_HierarchyRow = self.drWB_Hierarchy;
                    self.dbContext.add(esWB_Hierarchy_MDScope, aNewRow);
                    resolve(self);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeHierarchyMDScope(wbHierarchyScopeId: number): Promise<HierarchyBO> {
        const self = this;

        return new Promise<HierarchyBO>((resolve, error) => {
            _.forEach(self.WB_Hierarchy.WB_HierarchyMDScopeRows, (aRow) => {
                if (aRow.isActive && aRow.cif.WB_HIERARCHYSCR_ID == wbHierarchyScopeId) {
                    aRow.markDeleted();
                }
            });
            resolve(self);
        });
    }

    //<editor-fold desc = "Job Param">
    _jobParamForExport: IJobParam;
    _jobParamForBuilder: IJobParam;

    async IJobParam_OnLoadComplete(): Promise<void> {
        const jpMgr = new JobParamManager();
        const wbHierarchy: IwbHierarchy = this.WB_Hierarchy.cif;

        const jobParamSetting: Partial<IJobParamCreateLoadOptions> = {
            dbContext: this.dbContext,

            sourceType: "HierarchyBuilder",
            sourceWbId: wbHierarchy.WB_HIERARCHY_ID,

            appExecId: "HierarchyBuilder",
            wbTargetId: wbHierarchy.WB_HIERARCHY_ID,

            wbMasterDataSetId: wbHierarchy.WB_MDSET_ID,
            wbCompanyId: 0,
            wbSalesOrgId: 0,
        };

        this._jobParamForExport = await jpMgr.findOrCreate({
            ...jobParamSetting,
            JobSchedulerModuleId: "HierarchyExport",
            JobParamId: wbHierarchy.HIERARCHY_ID + "_Export",
            JobParamName: wbHierarchy.HIERARCHY_NAME + "_Export",
            JobProcessorType: "HierarchyExportProcessor",
            DependentTriggerId: "HierarchyExport",
        });

        this._jobParamForBuilder = await jpMgr.findOrCreate({
            ...jobParamSetting,
            JobSchedulerModuleId: "HierarchyBuilder",
            JobParamId: wbHierarchy.HIERARCHY_ID + "_Builder",
            JobParamName: wbHierarchy.HIERARCHY_NAME + "_Builder",
            JobProcessorType: "HierarchyBuilderProcessor",
            DependentTriggerId: "HierarchyBuilder",
            bDisabled: this.WB_Hierarchy.executionMode === 1, // batch: 0, manually: 1
        });
    }

    get JobParamForExport(): IJobParam {
        return this._jobParamForExport;
    }

    get JobParamForBuilder(): IJobParam {
        return this._jobParamForBuilder;
    }

    //<editor-fold>
}

PAEnvironment.registerBusinessObjectClass(HierarchyBO, "HierarchyBO", "Hierarchy");
